import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { SnackBarService } from '../../../core/services/snackbar.service';
import { LoggerService } from '../../../core/services/logger.service';
import { MessagingApiService } from '../../chat/services/messaging-api/messaging-api.service';
import { updateOwnerOnChannelsAndMessagesByID, updateUserOnUsers } from '../../chat/store/chat.actions';
import { AppStateWithChat } from '../../chat/store/chat.reducer';
import { setIsLoadingAway } from '../../shared/store/ui.actions';
import { IUserProfile } from '../interfaces/IUserProfile';
import { AuthenticationService } from '../services/authentication.service';
import {
  cancelAway,
  loadProfileInfo,
  sendAway,
  setAway,
  setCancelAway,
  setUser,
  unSetUser,
  updateUser,
} from './user.actions';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private messagingapiService: MessagingApiService,
    private authenticationService: AuthenticationService,
    private store: Store<AppStateWithChat>,
    private snackBarService: SnackBarService,
    private logger: LoggerService
  ) {}

  loadProfileInfo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadProfileInfo),
        switchMap(() => {
          return this.messagingapiService.getUser().pipe(
            mergeMap((user) => {
              this.store.dispatch(setUser({ profileInfo: user }));
              return of();
            }),
            catchError(() => {
              this.snackBarService.showErrorSnackBar('Failed get info, please try again');
              this.store.dispatch(unSetUser());
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUser),
        withLatestFrom(
          this.store.select((state) => {
            return state.user.user;
          })
        ),
        filter(([, user]) => user !== null),
        switchMap(([action, user]) => {
          const profileImageURL = action.attachments.length
            ? (action.attachments[0].thumbnailURL as string)
            : user!.subjectMessageableEntity.profileImageURL ?? '';
          const newUser: IUserProfile = {
            ...user!,
            subjectMessageableEntity: {
              ...user!.subjectMessageableEntity,
              displayName: action.displayName,
              profileImageURL,
            },
          };
          const ownerId =
            newUser.subjectMessageableEntity.businessReference?.entityUID ?? newUser.businessReference.entityUID;
          return this.messagingapiService.updateUser(action.displayName, action.attachments).pipe(
            mergeMap(() => {
              this.store.dispatch(setUser({ profileInfo: newUser }));
              this.store.dispatch(
                updateUserOnUsers({
                  id: ownerId,
                  displayName: newUser.subjectMessageableEntity.displayName,
                  profileImageURL,
                })
              );
              this.store.dispatch(
                updateOwnerOnChannelsAndMessagesByID({
                  ownerID: ownerId,
                  ownerDisplayName: newUser.subjectMessageableEntity.displayName,
                  ownerProfilePic: profileImageURL,
                })
              );
              this.snackBarService.showSuccesSnackbar('Profile updated');
              return [];
            }),
            catchError(() => {
              this.snackBarService.showErrorSnackBar('Error updating data');
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  sendAway$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendAway),
        switchMap(({ to, userId, message }) => {
          const awayUserId = userId ?? '';
          const awayMessage = message ?? '';
          this.store.dispatch(setIsLoadingAway({ value: true }));
          return this.messagingapiService.sendAway(to, awayUserId, awayMessage).pipe(
            mergeMap(() => {
              this.store.dispatch(setAway({ to, userId: awayUserId, message }));
              this.store.dispatch(setIsLoadingAway({ value: false }));
              this.snackBarService.showSuccesSnackbar('Away started');
              return [];
            }),
            catchError((err) => {
              this.logger.handleEffectError(err, 'sendAway');
              this.store.dispatch(setIsLoadingAway({ value: false }));
              this.snackBarService.showErrorSnackBar('Error on set away');
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  cancelAway$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cancelAway),
        switchMap(() => {
          return this.messagingapiService.cancelAway().pipe(
            mergeMap(() => {
              this.store.dispatch(setCancelAway());
              return [];
            }),
            catchError(() => {
              this.snackBarService.showErrorSnackBar('Error on set away');
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );
}
